import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import useApiRequest from "./useApiRequest";
import { Currency } from "./useFormattedCurrency";
import { HealthcareCompany, HealthcareCompanyType } from "@shared/types/commonTypes";
import { useCountries } from "./useCountries";

/**
 * Result of a tax calculation with both numeric values and formatted labels
 */
export interface TaxCalculationResult {
  basePrice: number;           // Price without tax
  taxAmount: number;           // Tax portion
  totalPrice: number;          // Price with tax
  taxRate: number;             // Tax rate percentage (e.g., 20 for 20%)
  basePriceLabel: string;      // Formatted: "€100.00"
  taxAmountLabel: string;      // Formatted: "€20.00"
  totalPriceLabel: string;     // Formatted: "€120.00"
}

/**
 * Hook that provides tax calculation functionality based on the current company's country tax rate.
 * Uses the company's country to determine the applicable tax rate.
 * 
 * @returns An object with:
 * - `calculateTax`: Function to calculate tax breakdown for a given price
 * - `taxRate`: The current tax rate percentage
 * - `isLoading`: Whether the data is still loading
 */
export const useTaxCalculator = () => {
  const { GET } = useApiRequest();
  const queryClient = useQueryClient();
  const { clinicId, eventOrganizerId } = useParams();
  
  // Subscribe to countries data for reactivity
  const { countries, isLoading: isCountriesLoading } = useCountries();
  
  // Determine company type and get from cache
  const companyId = clinicId || eventOrganizerId;
  const companyType = clinicId
    ? HealthcareCompanyType.DENTAL_CLINIC
    : HealthcareCompanyType.EVENT_ORGANIZER;
  
  const companies = queryClient.getQueryData<HealthcareCompany[]>([
    "healthcare-companies",
    companyType,
  ]) || [];
  
  const currentCompany = companies.find((company) => company.id === companyId);
  
  // Find country by company's countryISO3
  const country = useMemo(() => {
    if (!currentCompany?.address?.countryISO3 || countries.length === 0) {
      return null;
    }
    return countries.find((c) => c.iso3 === currentCompany.address.countryISO3) ?? null;
  }, [currentCompany, countries]);

  const isCountryLoaded = country !== null;
  const taxRate: number = country?.taxRate ?? 0;
  const taxRatePercent: number = taxRate * 100; // For display: 20 for 20%

  // Fetch currencies for proper formatting
  const { data: currencies = [], isLoading: isCurrenciesLoading } = useQuery<Currency[]>({
    queryKey: ["currencies"],
    queryFn: () => GET("/api/metadata/currencies"),
    // Currencies are static metadata - cache indefinitely (in-memory)
    staleTime: Infinity,
    // Keep in memory for 30 days even when not actively used (garbage collection)
    gcTime: 30 * 24 * 60 * 60 * 1000, // 30 days
  });

  // Overall loading state
  const isLoading = isCountriesLoading || !isCountryLoaded || isCurrenciesLoading;

  /**
   * Format a price value using the appropriate currency locale
   */
  const formatPrice = useCallback(
    (value: number, currencyIso: string): string => {
      const currency = currencies.find((c) => c.iso === currencyIso);

      if (!currency) {
        // Fallback formatting if currency not found
        return value.toLocaleString(undefined, {
          style: "currency",
          currency: currencyIso,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }

      if (currency.valueFormatLocale) {
        return new Intl.NumberFormat(currency.valueFormatLocale, {
          style: "currency",
          currency: currency.iso,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);
      }

      // Fallback to format template
      return currency.format.replace("{{value}}", value.toFixed(2));
    },
    [currencies]
  );

  /**
   * Calculate tax breakdown for a given price
   * 
   * @param price - The input price
   * @param currency - Currency ISO code (e.g., 'EUR', 'USD')
   * @param isInclusive - If true, the price already includes tax; if false, tax will be added
   * @returns TaxCalculationResult with numeric values and formatted labels
   */
  const calculateTax = useCallback(
    (price: number, currency: string, isInclusive: boolean): TaxCalculationResult => {
      // Return placeholder values while loading
      if (!isCountryLoaded) {
        return {
          basePrice: 0,
          taxAmount: 0,
          totalPrice: 0,
          taxRate: 0,
          basePriceLabel: "--",
          taxAmountLabel: "--",
          totalPriceLabel: "--",
        };
      }

      let basePrice: number;
      let taxAmount: number;
      let totalPrice: number;

      if (isInclusive) {
        // Price includes tax - extract the base price
        totalPrice = price;
        basePrice = price / (1 + taxRate);
        taxAmount = totalPrice - basePrice;
      } else {
        // Price excludes tax - add tax on top
        basePrice = price;
        taxAmount = basePrice * taxRate;
        totalPrice = basePrice + taxAmount;
      }

      // Round to 2 decimal places to avoid floating point issues
      basePrice = Math.round(basePrice * 100) / 100;
      taxAmount = Math.round(taxAmount * 100) / 100;
      totalPrice = Math.round(totalPrice * 100) / 100;

      return {
        basePrice,
        taxAmount,
        totalPrice,
        taxRate: taxRatePercent,
        basePriceLabel: formatPrice(basePrice, currency),
        taxAmountLabel: formatPrice(taxAmount, currency),
        totalPriceLabel: formatPrice(totalPrice, currency),
      };
    },
    [taxRate, taxRatePercent, formatPrice, isCountryLoaded]
  );

  return useMemo(
    () => ({
      calculateTax,
      taxRate: taxRatePercent,
      isLoading,
    }),
    [calculateTax, taxRatePercent, isLoading]
  );
};
